import { useApolloClient } from '@apollo/client/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Kitchen } from '../libs';
import { LIST_FOODS_BY_FILTERS_V2_QUERY } from '../libs/graphQL';
import type { ListFoodsByFiltersV2Query } from '../libs/types';

const BATCH_SIZE = 100;

const chunkIds = <T>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const serializeMealIdsByKey = (mealIdsByKey: Record<string, string[]>) =>
  Object.entries(mealIdsByKey)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, ids]) => `${key}:${[...new Set(ids.filter(Boolean))].sort().join(',')}`)
    .join('|');

const computeAverageForIds = (ids: string[], componentCountByMealId: Map<string, number>) => {
  let components = 0;
  let meals = 0;
  for (const id of ids) {
    const count = componentCountByMealId.get(id);
    if (count === undefined) continue;
    components += count;
    meals += 1;
  }
  return meals > 0 ? components / meals : 0;
};

interface UseMealComponentCountsByFoodIdsParams {
  kitchen: Kitchen;
  mealIdsByKey: Record<string, string[]>;
  enabled?: boolean;
}

const useMealComponentCountsByFoodIds = ({ kitchen, mealIdsByKey, enabled = true }: UseMealComponentCountsByFoodIdsParams) => {
  const client = useApolloClient();
  const [averageComponentsPerMealByKey, setAverageComponentsPerMealByKey] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const mealIdsByKeySerialized = useMemo(() => serializeMealIdsByKey(mealIdsByKey), [mealIdsByKey]);
  const mealIdsByKeyRef = useRef(mealIdsByKey);
  mealIdsByKeyRef.current = mealIdsByKey;

  useEffect(() => {
    if (!enabled || !kitchen || mealIdsByKeySerialized.length === 0) {
      if (mealIdsByKeySerialized.length === 0) {
        setAverageComponentsPerMealByKey({});
      }
      setLoading(false);
      return;
    }

    const mealIdsByKeySnapshot = mealIdsByKeyRef.current;
    const allUniqueIds = [...new Set(Object.values(mealIdsByKeySnapshot).flat().filter(Boolean))].sort();

    if (allUniqueIds.length === 0) {
      const emptyAverages = Object.fromEntries(Object.keys(mealIdsByKeySnapshot).map((key) => [key, 0]));
      setAverageComponentsPerMealByKey(emptyAverages);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      const componentCountByMealId = new Map<string, number>();
      try {
        for (const batch of chunkIds(allUniqueIds, BATCH_SIZE)) {
          const { data } = await client.query<
            ListFoodsByFiltersV2Query & {
              listFoodsByFiltersV2?: { data?: Array<{ id: string; components?: Array<{ id: string }> | null }> };
            }
          >({
            query: LIST_FOODS_BY_FILTERS_V2_QUERY,
            variables: {
              filters: { kitchen, ids: batch },
              page: 0,
              limit: Math.max(batch.length, 100)
            },
            fetchPolicy: 'no-cache'
          });

          for (const food of data?.listFoodsByFiltersV2?.data ?? []) {
            componentCountByMealId.set(food.id, food.components?.length ?? 0);
          }
        }

        if (!cancelled) {
          const averages: Record<string, number> = {};
          for (const [key, ids] of Object.entries(mealIdsByKeySnapshot)) {
            averages[key] = computeAverageForIds(ids, componentCountByMealId);
          }
          setAverageComponentsPerMealByKey(averages);
        }
      } catch {
        if (!cancelled) {
          setAverageComponentsPerMealByKey({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, enabled, kitchen, mealIdsByKeySerialized]);

  return { averageComponentsPerMealByKey, mealComponentCountsLoading: loading };
};

export default useMealComponentCountsByFoodIds;
